// RedemptionList.jsx

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";

// const STATUS_CONFIG = {
//     Pending:  { label: "⏳ Pending",  cls: "bg-yellow-900/30 text-yellow-400 border-yellow-700/40" },
//     Approved: { label: "✅ Approved", cls: "bg-green-900/30  text-green-400  border-green-700/40"  },
//     Redeemed: { label: "🎉 Redeemed", cls: "bg-purple-900/30 text-purple-400 border-purple-700/40" },
//     Rejected: { label: "❌ Rejected", cls: "bg-red-900/30    text-red-400    border-red-700/40"    },
// };

const STATUS_CONFIG = {
    VoucherCreated:      { label: "⏳ Awaiting Redemption", cls: "bg-yellow-900/30 text-yellow-400 border-yellow-700/40" },
    Redeemed:            { label: "🎉 Redeemed",            cls: "bg-purple-900/30 text-purple-400 border-purple-700/40" },
    CancelledByCustomer: { label: "🚫 Cancelled",            cls: "bg-gray-800/60  text-gray-400  border-gray-700/40" },
    Expired:             { label: "⌛ Expired",              cls: "bg-red-900/30    text-red-400    border-red-700/40" },
};

const RedemptionList = () => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;
    const user  = authState?.user;

    const [records,  setRecords]  = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [filter,   setFilter]   = useState("ALL");
    const [expanded, setExpanded] = useState(null);

    useEffect(() => { loadRedemptions(); }, []);

    const loadRedemptions = async () => {
        try {
            setLoading(true);
            const res = await fetchData({
                url: "/pd/streaks/redemption-my",
                method: "post",
                data: { customerId: user?._id },
                headers: { Authorization: token },
            });
            if (res?.success) setRecords(res.records || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // const filtered = filter === "ALL"
    //     ? records
    //     : filter === "Redeemed"
    //         ? records.filter((r) => r.status === "Redeemed" || r.status === "Approved")
    //         : records.filter((r) => r.status === filter);

    // const stats = {
    //     total:    records.length,
    //     pending:  records.filter((r) => r.status === "Pending").length,
    //     redeemed: records.filter((r) => r.status === "Redeemed").length,
    //     rejected: records.filter((r) => r.status === "Rejected").length,
    //     totalPts: records.filter((r) => r.status === "Redeemed")
    //                      .reduce((s, r) => s + (r.approvedPoints || r.requestedPoints || 0), 0),
    // };

    const filtered = filter === "ALL" ? records : records.filter((r) => r.status === filter);

    const stats = {
        total:    records.length,
        pending:  records.filter((r) => r.status === "VoucherCreated").length,
        redeemed: records.filter((r) => r.status === "Redeemed").length,
        rejected: records.filter((r) => r.status === "CancelledByCustomer").length,
        totalPts: records.filter((r) => r.status === "Redeemed")
                         .reduce((s, r) => s + (r.approvedPoints || r.requestedPoints || 0), 0),
    };

    if (loading) {
        return (
            <div className="space-y-2">
                {[1,2,3].map((i) => (
                    <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4 animate-pulse">
                        <div className="h-3 bg-gray-800 rounded w-1/2 mb-2" />
                        <div className="h-3 bg-gray-800 rounded w-1/3" />
                    </div>
                ))}
            </div>
        );
    }

    if (!records.length) {
        return (
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center">
                <div className="text-3xl mb-2">🎁</div>
                <div className="text-gray-200 text-sm">No redemptions yet</div>
                <div className="text-gray-300 text-xs mt-1">
                    Approve claims and redeem your streak points
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3 bg-gray-900 p-2 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <div className="grid grid-cols-4 gap-2">
                {[
                    { label: "Total",    value: stats.total,    color: "text-white"   },
                    { label: "Pending",  value: stats.pending,  color: "text-yellow-400" },
                    { label: "Redeemed", value: stats.redeemed, color: "text-purple-400" },
                    { label: "Pts Used", value: stats.totalPts, color: "text-green-400"  },
                ].map((s) => (
                    <div key={s.label} className="bg-gray-900 border border-gray-800 rounded-xl p-2 text-center">
                        <div className={`font-black text-base ${s.color}`}>{s.value}</div>
                        <div className="text-[10px] text-gray-300">{s.label}</div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap pb-1">
                {/* {["ALL", "Pending", "Redeemed", "Rejected"].map((f) => ( */}
                {["ALL", "VoucherCreated", "Redeemed", "CancelledByCustomer"].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`w-fit px-2 py-1 rounded-md text-xs border transition-colors ${
                            filter === f
                                ? "bg-cyan-500 border-cyan-500 text-black font-bold"
                                : "bg-gray-800 border-gray-700 text-gray-200"
                        }`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {/* Records */}
            <AnimatePresence>
                {filtered.map((redeem) => {
                    const cfg = STATUS_CONFIG[redeem.status] || STATUS_CONFIG.VoucherCreated;
                    const isExpanded = expanded === redeem._id;

                    return (
                        <motion.div
                            key={redeem._id}
                            layout
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden"
                        >
                            <button
                                onClick={() => setExpanded(isExpanded ? null : redeem._id)}
                                className="w-fit px-2 py-1 rounded-md text-left"
                            >
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="text-sm font-bold text-white">
                                            🏪 {redeem.shopName || redeem.shopkeeperName}
                                        </div>
                                        <div className="text-[11px] text-gray-200 mt-0.5">
                                            {redeem.shopkeeperCode}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-purple-300 font-black">
                                            {redeem.approvedPoints || redeem.requestedPoints}
                                            <span className="text-[10px] text-gray-300 font-normal ml-1">pts</span>
                                        </div>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${cfg.cls}`}>
                                            {cfg.label}
                                        </span>
                                    </div>
                                </div>
                            </button>

                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: "auto" }}
                                        exit={{ height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-3 pb-3 pt-0 border-t border-gray-800 space-y-2">
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-100 mt-2">
                                                <div>
                                                    <span className="text-gray-300">Requested: </span>
                                                    <span className="font-bold">{redeem.requestedPoints} pts</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-300">Approved: </span>
                                                    <span className="text-green-400 font-bold">
                                                        {redeem.approvedPoints || "-"} pts
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-300">Type: </span>
                                                    {redeem.redemptionType}
                                                </div>
                                                <div>
                                                    <span className="text-gray-300">Requested on: </span>
                                                    {new Date(redeem.createdAt).toLocaleDateString("en-IN")}
                                                </div>
                                                {/* FIX: now uses redeemedAt from schema */}
                                                {redeem.redeemedAt && (
                                                    <div className="col-span-2">
                                                        <span className="text-gray-300">Redeemed on: </span>
                                                        <span className="text-purple-400">
                                                            {new Date(redeem.redeemedAt).toLocaleDateString("en-IN")}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            {redeem.approvalRemarks && (
                                                <div className="text-xs text-yellow-300 bg-yellow-900/20 border border-yellow-700/30 p-2 rounded-lg">
                                                    📝 {redeem.approvalRemarks}
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
};

export default RedemptionList;
